import { notification } from 'antd';
import { Property, Schema } from 'hooks/projects';

const IsUniqueValue = '{{$isUnique}}';

export enum RelationalCondition {
  NONE = 'NONE',
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  IS_ONE_OF = 'IS_ONE_OF',
  IS_NOT_ONE_OF = 'IS_NOT_ONE_OF',
  HASVALUE = 'HASVALUE',
  CONTAINS = 'CONTAINS',
  EXISTS = 'EXISTS',
  NOT_EXISTS = 'NOT_EXISTS',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_THAN_EQUALS = 'GREATER_THAN_EQUALS',
  LESS_THAN = 'LESS_THAN',
  LESS_THAN_EQUALS = 'LESS_THAN_EQUALS',
  STARTS_WITH = 'STARTS_WITH',
  NOT_STARTS_WITH = 'NOT_STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH',
  NOT_ENDS_WITH = 'NOT_ENDS_WITH',
  REGEX = 'MATCHES',
  NOT_REGEX = 'NOT_MATCHES',
  IS_UNIQUE = 'IS_UNIQUE',
}

export interface RelationalRule {
  _type: 'RelationalRule';
  condition: RelationalCondition;
  property: string;
  value: string;
  fieldType: string;
  isPropertyComparison?: boolean;
  isManyToMany?: boolean;
  nestedLevel?: number;
  hasChildren?: boolean;
}

type InternalFilterValue =
  | string
  | string[]
  | number
  | number[]
  | boolean
  | boolean[]
  | InternalFilter
  | InternalFilter[];
interface InternalFilter {
  [key: string]: InternalFilterValue;
}

export enum LogicalCondition {
  AND = 'and',
  OR = 'or',
}

export interface LogicalRule {
  _type: 'LogicalRule';
  condition: LogicalCondition;
  children: Array<RelationalRule | LogicalRule>;
}

/**
 * Checks if a value should be displayed in UI for a given condition.
 * E.g.: 'Exists' has no visible value label, while 'Equals' has it.
 */
export function isConditionWithValueLabel(condition: RelationalCondition) {
  return ![
    RelationalCondition.EXISTS,
    RelationalCondition.NOT_EXISTS,
    RelationalCondition.IS_UNIQUE,
  ].includes(condition);
}

export function isRelationalRuleComplete(rule: RelationalRule): boolean {
  if (!rule.property) return false;
  if (!isConditionWithValueLabel(rule.condition)) return true;
  return rule.value.trim().length > 0;
}

export function logicalRuleToFilter(
  rule: LogicalRule,
  schemaAttributes: Property[]
): InternalFilter {
  const childrenFilter = rule.children
    .filter(
      (child) =>
        child._type !== 'RelationalRule' || isRelationalRuleComplete(child)
    )
    .map((child) =>
      child._type === 'LogicalRule'
        ? logicalRuleToFilter(child, schemaAttributes)
        : relationalRuleToFilter(child, schemaAttributes, 0)
    );
  const { condition } = rule;

  return {
    [condition]: childrenFilter,
  };
}

const valueHasCurlyBracketsRHRC = (value: string) => /^{{(.+)}}$/.test(value);

function parseConditionValue(
  type: string,
  value: string,
  split: boolean = false
): InternalFilterValue {
  value = value?.replaceAll('!', '');
  if (split) {
    switch (type) {
      case 'String':
        return value.split(',');

      case 'Int':
        return value.split(',').map((val) => parseInt(val, 10) || 0);

      case 'Float':
        return value.split(',').map((val) => parseFloat(val) || 0);

      case 'Boolean':
        return value.split(',').map((val) => val === 'true');
    }
  } else {
    switch (type) {
      case 'Int':
        return parseInt(value, 10) || 0;

      case 'Float':
        return parseFloat(value) || 0;

      case 'Boolean':
        return value === 'true';
    }
  }
  return value;
}

function haveChildren(variable: any): boolean {
  const vType = typeof variable;
  const basicTypes = ['string', 'boolean', 'number'];
  if (basicTypes.includes(vType)) return true;
  if (Array.isArray(variable)) {
    if (variable.length === 0) {
      return true;
    }
    return haveChildren(variable[0]);
  }
  return false;
}

function relationalRuleToFilter(
  rule: RelationalRule,
  schemaAttributes: Property[],
  nestingLevel: number
): InternalFilter {
  const filter: InternalFilter = {};

  if (rule.property.includes('.')) {
    const property = rule.property.substring(0, rule.property.indexOf('.'));

    // ManytoMany was used, we need to add extra "items"
    const isMany2Many = rule.isManyToMany && nestingLevel === rule.nestedLevel;
    const needExtraItems = !!isMany2Many;

    const childProperty = rule.property.substring(
      rule.property.indexOf('.') + 1,
      rule.property.length
    );

    if (needExtraItems && !rule.property.includes('.items')) {
      filter[property] = {
        items: relationalRuleToFilter(
          { ...rule, property: childProperty },
          schemaAttributes,
          nestingLevel + 1
        ),
      };
    } else {
      filter[property] = relationalRuleToFilter(
        { ...rule, property: childProperty },
        schemaAttributes,
        nestingLevel + 1
      );
    }

    return filter;
  }

  const ruleValue = rule.isPropertyComparison
    ? `{{${rule.value}}}`
    : rule.value;
  if (rule.isPropertyComparison) {
    rule.fieldType = 'String';
  }

  switch (rule.condition) {
    case RelationalCondition.EQUALS:
      filter[rule.property] = {
        eq: parseConditionValue(rule.fieldType, ruleValue),
      };
      return filter;
    case RelationalCondition.NOT_EQUALS:
      filter.not = {
        [rule.property]: {
          eq: parseConditionValue(rule.fieldType, ruleValue),
        },
      };
      return filter;
    case RelationalCondition.IS_ONE_OF:
      filter[rule.property] = {
        in: parseConditionValue(rule.fieldType, ruleValue, true),
      };
      return filter;

    case RelationalCondition.IS_NOT_ONE_OF:
      filter.not = {
        [rule.property]: {
          in: parseConditionValue(rule.fieldType, ruleValue, true),
        },
      };
      return filter;
    case RelationalCondition.HASVALUE:
    case RelationalCondition.CONTAINS:
      filter[rule.property] = {
        contains: parseConditionValue(rule.fieldType, ruleValue, true),
      };
      return filter;
    case RelationalCondition.EXISTS:
      filter[rule.property] = {
        isNull: false,
      };
      return filter;
    case RelationalCondition.NOT_EXISTS:
      filter[rule.property] = {
        isNull: true,
      };
      return filter;
    case RelationalCondition.GREATER_THAN:
      filter[rule.property] = {
        gt: parseConditionValue(rule.fieldType, ruleValue),
      };
      return filter;
    case RelationalCondition.GREATER_THAN_EQUALS:
      filter[rule.property] = {
        gte: parseConditionValue(rule.fieldType, ruleValue),
      };
      return filter;
    case RelationalCondition.LESS_THAN:
      filter[rule.property] = {
        lt: parseConditionValue(rule.fieldType, ruleValue),
      };
      return filter;

    case RelationalCondition.LESS_THAN_EQUALS:
      filter[rule.property] = {
        lte: parseConditionValue(rule.fieldType, ruleValue),
      };
      return filter;
    case RelationalCondition.STARTS_WITH:
      filter[rule.property] = {
        prefix: parseConditionValue(rule.fieldType, ruleValue),
      };
      return filter;
    case RelationalCondition.NOT_STARTS_WITH:
      filter.not = {
        [rule.property]: {
          prefix: parseConditionValue(rule.fieldType, ruleValue),
        },
      };
      return filter;
    case RelationalCondition.ENDS_WITH:
      filter[rule.property] = {
        postfix: parseConditionValue(rule.fieldType, ruleValue),
      };
      return filter;
    case RelationalCondition.NOT_ENDS_WITH:
      filter.not = {
        [rule.property]: {
          postfix: parseConditionValue(rule.fieldType, ruleValue),
        },
      };
      return filter;
    case RelationalCondition.REGEX:
      filter[rule.property] = {
        matches: parseConditionValue(rule.fieldType, ruleValue),
      };
      return filter;

    case RelationalCondition.NOT_REGEX:
      filter.not = {
        [rule.property]: {
          matches: parseConditionValue(rule.fieldType, ruleValue),
        },
      };
      return filter;
    case RelationalCondition.IS_UNIQUE:
      filter[rule.property] = {
        eq: IsUniqueValue,
      };
      return filter;
    default:
      return {};
  }
}

// adds a nested property to existing propoerty string
// example:  movie -> movie.title
function appendNestedProperty(nestedKey: string, property: string) {
  if (!nestedKey) {
    return property;
  }
  // When it's first nested key to it would be the first property without dots
  if (!property) {
    return nestedKey;
  }
  return `${property}.${nestedKey}`;
}

const isProperty = (propertyName: string, schema?: Schema) =>
  schema?.properties.find((p) => p.name === propertyName);

function filterToRelationalRule(
  key: string,
  value: InternalFilter,
  schema?: Schema
): RelationalRule {
  let itr: InternalFilterValue = value;
  let previous: { itr: null | InternalFilterValue; key: string } = {
    itr: null,
    key: '',
  };
  let depth = 1;
  let property = key !== 'not' ? key : '';
  let conditionString = '';
  let valueString = '';
  let isNegative = key === 'not';
  let schemaItr =
    schema?.properties.find((prop) => prop.name === key)?.schema || schema;
  let fieldType = 'String';
  let nestedLevel = 0;
  let isMany2Many = false;

  while (depth < 100) {
    depth += 1;
    if (typeof itr !== 'object') {
      break;
    }
    if (Array.isArray(itr) && itr.some((child) => typeof child === 'object'))
      throw new Error('Invalid filter');
    const keys: string[] = Object.keys(itr);
    if (keys.length > 1 || keys.length < 0) throw new Error('Invalid filter');

    const childKey = keys[0];
    const child = itr[childKey];

    const keyIsProperty = isProperty(childKey, schemaItr);

    if (childKey === 'items' && !keyIsProperty) {
      nestedLevel = depth - 2;
      isMany2Many = true;
    }

    if (haveChildren(child)) {
      property = appendNestedProperty(previous.key, property);
      if (childKey === 'isNull') {
        valueString = child ? 'true' : 'false';
      } else if (Array.isArray(child)) {
        valueString = child.join(',');
      } else {
        // in future this value string would be changed to a type
        // Keep track if the type of the attribute is boolean or not. If the value is boolean, we want to show it as it is.
        const isBoolean = typeof child === 'boolean';
        valueString = isBoolean ? `${child}` : `${child ?? ''}`;
      }
      conditionString = childKey;
      const lastProperty = previous.key || property;
      fieldType =
        schemaItr?.properties.find((prop) => prop.name === lastProperty)
          ?.type ??
        schema?.properties.find((prop) => prop.name === lastProperty)?.type ??
        fieldType;

      break;
    }
    if (childKey === 'not') {
      isNegative = true;
    } else {
      const childSchema = schemaItr?.properties.find(
        (prop) => prop.name === childKey
      )?.schema;
      if (childSchema) schemaItr = childSchema;

      property = appendNestedProperty(previous.key, property);
      previous = { key: childKey, itr };
    }
    itr = itr[childKey] as InternalFilter;
  }
  let condition = RelationalCondition.EQUALS;
  conditionString = `${isNegative ? 'not_' : ''}${conditionString}`;

  switch (conditionString) {
    case 'isNull':
      condition =
        valueString === 'false'
          ? RelationalCondition.EXISTS
          : RelationalCondition.NOT_EXISTS;
      break;
    case 'eq':
      condition =
        valueString === IsUniqueValue
          ? RelationalCondition.IS_UNIQUE
          : RelationalCondition.EQUALS;
      break;
    case 'not_eq':
      condition = RelationalCondition.NOT_EQUALS;
      break;
    case 'in':
      condition = RelationalCondition.IS_ONE_OF;
      break;
    case 'not_in':
      condition = RelationalCondition.IS_NOT_ONE_OF;
      break;
    case 'CONTAINS':
      condition = RelationalCondition.CONTAINS;
      break;
    case 'gt':
      condition = RelationalCondition.GREATER_THAN;
      break;
    case 'gte':
      condition = RelationalCondition.GREATER_THAN_EQUALS;
      break;
    case 'lt':
      condition = RelationalCondition.LESS_THAN;
      break;
    case 'lte':
      condition = RelationalCondition.LESS_THAN_EQUALS;
      break;
    case 'prefix':
      condition = RelationalCondition.STARTS_WITH;
      break;
    case 'not_prefix':
      condition = RelationalCondition.NOT_STARTS_WITH;
      break;
    case 'ENDS_WITH':
      condition = RelationalCondition.ENDS_WITH;
      break;
    case 'NOT_ENDS_WITH':
      condition = RelationalCondition.NOT_ENDS_WITH;
      break;
    case 'matches':
      condition = RelationalCondition.REGEX;
      break;
    case 'not_matches':
      condition = RelationalCondition.NOT_REGEX;
      break;
    default:
      condition = RelationalCondition.EQUALS;
      break;
  }
  const isPropertyComparison = valueHasCurlyBracketsRHRC(valueString);
  valueString = isPropertyComparison
    ? valueString.toString().replace('{{', '').replace('}}', '')
    : valueString;

  return {
    _type: 'RelationalRule',
    property,
    condition,
    value: valueString,
    fieldType,
    isPropertyComparison,
    isManyToMany: isMany2Many,
    nestedLevel,
  } as RelationalRule;
}

function isValidLogicalFilter(key: string, filter: InternalFilterValue) {
  if (key !== LogicalCondition.AND && key !== LogicalCondition.OR) {
    return false;
  }
  if (!Array.isArray(filter)) return false;
  if (
    filter.some(
      (value: string | boolean | number | InternalFilter) =>
        typeof value !== 'object'
    )
  ) {
    return false;
  }
  return filter as InternalFilter[];
}

function filterToLogicalRule(
  key: LogicalCondition.AND | LogicalCondition.OR,
  values: InternalFilter[],
  schema?: Schema
): LogicalRule | RelationalRule {
  return {
    _type: 'LogicalRule',
    condition: key,
    children: values.map((value) => {
      if (Object.keys(value).length > 1 || Object.keys(value).length < 0)
        throw new Error('Invalid filter');
      return Object.keys(value).map((childKey) => {
        if (
          childKey === LogicalCondition.AND ||
          childKey === LogicalCondition.OR
        ) {
          const filter = isValidLogicalFilter(childKey, value[childKey]);
          if (!filter) {
            throw new Error('Invalid filter');
          }
          return filterToLogicalRule(childKey, filter, schema);
        }
        return filterToRelationalRule(
          childKey,
          value[childKey] as InternalFilter,
          schema
        );
      })[0];
    }),
  } as LogicalRule;
}

// Always use try catch with this one.
export function parseFilterToLogicalRule(
  filterString: string,
  schema?: Schema,
  shouldThrowError: boolean = false
): LogicalRule {
  try {
    const filter: { [key: string]: InternalFilter[] } =
      JSON.parse(filterString);
    const children = Object.keys(filter).map((key) =>
      filterToLogicalRule(key as LogicalCondition.AND, filter[key], schema)
    );
    if (children[0]._type === 'LogicalRule') return children[0];
    return {
      _type: 'LogicalRule',
      condition: LogicalCondition.AND,
      children,
    };
  } catch (error: any) {
    if (shouldThrowError) {
      throw error;
    } else {
      notification.error({
        message: 'Failed to parse the filters.',
        description: error?.message,
      });
      return {
        _type: 'LogicalRule',
        condition: LogicalCondition.AND,
        children: [],
      };
    }
  }
}
